import { BackendDataApiResponse, BackendItemsApiResponse } from '@shared/core/services/overbooking-api.service';
import { EntityId, EntityUuid } from '@shared/core/types';
import { CalendarEvent } from 'angular-calendar';
import { WeekDay } from '@angular/common';
import { FormFieldOptions } from '@shared/shared/form-fields';
import { ValidatorFn } from '@angular/forms';

import { FieldTypes } from './enums';
import { ItemNode } from './interfaces';

export interface StartEndDate<T> {
  startDate: T;
  endDate: T;
}

export interface OperatorsScheduleWorkingHoursEditDialogData {
  operatorId: EntityId;
  startsAt?: Date;
}

export interface OperatorsScheduleActivityEditDialogData {
  operatorId: EntityId;
  activity?: OperatorsScheduleCalendarActivity;
  startsAt?: Date;
}

export type OperatorsScheduleCalendarActivity = CalendarEvent<EventData>;
export type OperatorsScheduleCalendarWorkingHours = {
  day: WeekDay;
};

export interface ManagerGroup {
  uuid: string;
  name: string;
  data: ManagerData;
  operators: ManagerGroupOperator[];
  temporaryOperators?: ManagerGroupOperator[];
}

export interface UserList {
  id: number;
  name: string;
  surname: string;
}

export interface ManagerGroupOperator extends UserList {
  data: OperatorData;
  events: EventData[];
  schedules: DutyData[];
  slots?: Slot[];
  groupName: string;
  groupSupervisorName: string;
}

export interface ScheduleData {
  time_on_duty: DutyData[];
  slots_schedule: SlotsData | null;
  events: EventData[];
}

export interface SuccessResponse {
  success: boolean;
}

export type EventTypesResponse = BackendItemsApiResponse<EventType[]>;
export type ServiceItemsResponse = BackendDataApiResponse<ServiceItem[]>;

export interface ActivitySaveRequest {
  typeCode: string;
  start: Date | string;
  end: Date | string;
  operatorsIds: number[];
  regular: boolean;
  comment: string;
  educationServiceId?: number;
  onlyForOneDate?: boolean;
}

export interface Reason {
  id: number;
  text: string;
}

export enum EventAction {
  create = 'create',
  edit = 'edit',
  delete = 'delete',
}

export interface OptionItem {
  label: string;
  value: number | string | Object | null;
}

export interface ServiceItem {
  id: number;
  subject: string;
  hasLowLevel: boolean;
}

export interface ScheduleEditable {
  previous: DutyData | null;
  current: DutyData | null;
  index: number;
  onlyForOneDate: boolean | null;
}

export enum EventKind {
  timeOnDuty = 'timeOnDuty',
  activities = 'activities',
}

export interface TimeInterval {
  time: Date;
  isInSlots: boolean;
}

export interface OperatorData extends ManagerData {
  factMonthHours: number;
  plannedMonthHours: number;
}

export interface CancelIntroLessonRequest {
  absenceReasonId: number;
  absenceComment: string;
}

export interface ManagerAllGroup {
  uuid: string;
  name: string;
}

export interface RequestState {
  empty: boolean;
  fetching: boolean;
  error: boolean;
}

export interface OperatorKind {
  id: string;
  name: string;
}

export interface OperatorGroup {
  id: string;
  name: string;
}

export interface OperatorSchedule {
  uuid: string;
  label: string;
}

export interface DistributionStrategy {
  uuid: string;
  label: string;
}

export interface ManagerAllGroupsResponse extends SuccessResponse {
  items: ManagerAllGroup[];
}

export interface TimeOfDutyUpdateParams {
  schedule: DutyData[];
  date: string;
}

export interface TimeOfDutyUpdateResponse {
  data: TimeOfDutyUpdateParams[];
}

export interface WfmGroupItem {
  id: EntityUuid;
  name: string;
  parent: string;
}

export interface WfmGroup {
  id: EntityUuid;
  name: string;
  parentGroupId: string;
  members: Members[];
}

export interface Members {
  avatarUrl: string | null;
  id: number;
  name: string;
  surname: string;
}

export interface WfmNewGroup {
  name: string;
}

export interface SearchOperatorItem {
  id: EntityId;
  fullName: string;
}

export interface WfmGroupUpdate {
  uuid: EntityUuid;
  name: string;
  parentUuid: string;
}

export type WfmGroupsListResponse = BackendItemsApiResponse<WfmGroupItem[]>;
export type WfmGroupsList = WfmGroupItem[];
export type WfmGroupResponse = BackendDataApiResponse<WfmGroup>;
export type WfmSearchOperatorResponse = BackendItemsApiResponse<SearchOperatorItem[]>;

export interface StartEndDate<T> {
  startDate: T;
  endDate: T;
}

export interface OperatorsScheduleWorkingHoursEditDialogData {
  operatorId: EntityId;
  startsAt?: Date;
}

export interface OperatorsScheduleActivityEditDialogData {
  operatorId: EntityId;
  activity?: OperatorsScheduleCalendarActivity;
  startsAt?: Date;
}

export interface EventData {
  uuid: string | null;
  serialUuid: string | null;
  type: EventType;
  title: string;
  regular: boolean;
  // UTC date
  start: Date | string;
  // UTC date
  end: Date | string;
  comment: string;
  operatorsIds?: number[];
  educationServiceId?: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: { [key: string]: any };
  groupActivityUuid?: string;
  groupUuid?: string;
  operators: Members[];
}

export interface Slot {
  time: Date | string;
  duration: number;
  schedules: string[];
  events: string[];
  availableResource: number | null;
}

export enum DutyTypeCode {
  regular = 'regular',
  once_only = 'once_only',
  overtime = 'overtime',
}

export interface DutyType {
  readonly code: DutyTypeCode;
  readonly label: string;
  readonly description: string;
  readonly color: string;
  readonly isRegular: boolean;
}

export interface DutyData {
  index?: number;
  uuid: string | null;
  serialUuid: string | null;
  start: Date;
  end: Date;
  isRegular?: boolean;
  dutyType: DutyTypeCode;
  rate?: string;
}

export interface EventType {
  availableForRegularity: boolean;
  code: string;
  label?: string;
  description?: string;
  color?: string;
  mode?: string;
  needConfirm?: boolean;
  isNotify?: boolean;
}

export interface ScheduleData {
  time_on_duty: DutyData[];
  slots_schedule: SlotsData | null;
  events: EventData[];
}

export interface SlotsData {
  interval: number;
  start: Date | string | null;
  end: Date | string | null;
  serverTime: Date | string | null;
}

export interface SuccessResponse {
  success: boolean;
}

export interface ManagerGroupsResponse extends SuccessResponse {
  groups: ManagerGroup[];
  slots: Slot[];
  metrics: Metrics;
}

export interface Metrics {
  sla: number;
  prediction: number;
  plan: number;
  adequacy: number;
  correlation: number;
  slots: { time: string; sla: number }[];
}

export interface ManagerData {
  introLessonsHours: number;
  reservationsHours: number;
  openHoursCount: number;
}

export interface ManagerAllGroup {
  uuid: string;
  name: string;
}

export interface OperatorItem {
  id: number;
  name: string;
  surname: string;
  operatorKind: OperatorKind;
  operatorGroup: OperatorGroup;
  operatorSchedule: OperatorSchedule;
  distributionStrategy: DistributionStrategy;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  calendar?: any;
}

export interface OperatorKind {
  uuid: string;
  name: string;
  parentUuid: string;
}

export interface OperatorGroup {
  id: string;
  name: string;
}

export interface OperatorSchedule {
  uuid: string;
  label: string;
}

export interface DistributionStrategy {
  uuid: string;
  label: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface IRequestParams {
  page?: number;
  count?: number;
  total?: number;
  sortCriteria?: string | null;
  filterCriteria?: string | null;
}

export interface SortCriteria {
  sort: string;
  column: string;
}

export interface StoreParams {
  pager: IPager;
  sortCriteria: SortCriteria | null;
  requestProgress: IRequestState;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface IPager {
  page: number;
  count: number;
  total: number;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface IRequestState {
  empty: boolean;
  fetching: boolean;
  error: boolean;
}

export interface RequestParams {
  page?: number;
  count?: number;
  total?: number;
  sortCriteria?: string | null;
  filterCriteria?: string | null;
}

export interface SortCriteriaInterface {
  sort: string;
  column: string;
}

export interface AllMethodistsSchedulesResponse extends ResponseParams {
  items: MethodistSchedule[];
}

export interface ResponseParams {
  sortCriteria: SortCriteriaInterface | null;
  page: number;
  count: number;
  total: number;
}

export interface MethodistSchedule {
  uuid: string;
  label: string;
}

export interface UpdateWfmOperator {
  operatorId: number;
  name: string;
  surname: string;
  operatorKindUuid: string;
  slotScheduleUuid: string;
  distributionStrategyUuid: string;
  operatorGroupUuid: string;
}

export interface DefaultFiltersInterface {
  group: string | null;
  schedule: string | null;
  id: number | null;
}

export interface OperatorsResponse extends ResponseParams {
  items: OperatorItem[];
}

export interface DataParams {
  /*
   * field code
   */
  code: string;
  /*
   * field name (label)
   */
  label: string;
  /*
   *  visible table cell
   */
  visible: boolean;
  /*
   * form field required
   */
  isRequired: boolean;
  /*
   * form field types
   */
  fieldType: FieldTypes | null;
  /*
   * form field visibility
   */
  fieldHidden?: boolean;
  /*
   * options for select\multiselect fieldType
   */

  fieldOptions?: FormFieldOptions;
  /*
   * identify field for object option
   */
  fieldOptionIdentify?: string;
  /*
   * form validators
   */
  fieldValidations?: ValidatorFn[];
  /*
   * form field disabled
   */
  fieldDisabled?: () => boolean;
  /*
   * special params for any fieldType
   * * fieldDateOutputFormat
   * * fieldDateFormat
   * * fieldDateStartView
   * * fieldDateOnlyMonth
   * * fieldSelectAllowNullValue
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldExtParams?: any;
  /**
   * default value on creation
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
  /**
   * callback function
   */
  fieldCallbackFn?: Function;
}

export interface OperatorMertics {
  days: SlaMertics;
  weeks: SlaMertics;
}

export interface SlaMetricsChunk {
  time?: string;
  plan: number;
  prediction: number;
  adequacy: number;
  correlation: number;
  sla: number;
}

export interface SlaMertics extends SlaMetricsChunk {
  items: SlaMetricsChunk[];
}

export interface ManagerHierarchyGroupsResponse {
  items: ItemNode[];
}

export interface GroupActivitiesResponse {
  items: EventData[];
}

export interface DeleteScheduleCheckResponse {
  success: boolean;
  activitiesCount: number;
}
